
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { agrogestao } from "@/api/agrogestaoClient";
import {
  LayoutDashboard,
  Landmark,
  Wallet,
  Wheat,
  Package,
  Calculator,
  FileText,
  FileCheck,
  Sparkles,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Tractor,
  User,
  Receipt,
  TrendingUp,
  Truck,
  Search,
  MapPin
} from "lucide-react";
import BuscaGlobal from "../components/shared/BuscaGlobal";
import { TopNav } from "./LandingPage";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Layout({ children, currentPageName }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [landingMenuOpen, setLandingMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [propriedades, setPropriedades] = useState([]);
  const [propriedadeSelecionada, setPropriedadeSelecionada] = useState(null);
  const [buscaOpen, setBuscaOpen] = useState(false);

  useEffect(() => {
    const init = async () => {
      const u = await agrogestao.auth.me();
      if (u) {
        loadUser();
        loadPropriedades();
      }
    };
    init();
    
    // Atalho de teclado para busca global (Ctrl+K ou Cmd+K)
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setBuscaOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadUser = async () => {
    try {
      const userData = await agrogestao.auth.me();
      if (userData) {
        setUser(userData);
      }
    } catch (e) {
      // User likely not logged in or session expired
    }
  };

  const loadPropriedades = async () => {
    try {
      const u = await agrogestao.auth.me();
      if (!u) return;

      const attempted = localStorage.getItem('owner_backfill_attempted');
      if (u.email && !attempted) {
        try {
          await agrogestao.functions.invoke('backfillPropriedadesOwner', { email: u.email });
        } catch { } finally {
          localStorage.setItem('owner_backfill_attempted', '1');
        }
      }
      
      // Attempt to list properties via function
      const response = await agrogestao.functions.invoke('listarPropriedadesUsuario').catch(() => ({ data: { success: false } }));
      
      if (response.data?.success) {
        const props = response.data.propriedades;
        setPropriedades(props);
        if (props.length > 0) {
          const saved = localStorage.getItem("propriedade_selecionada");
          if (saved && props.find(p => p.id === saved)) {
            setPropriedadeSelecionada(props.find(p => p.id === saved));
          } else {
            setPropriedadeSelecionada(props[0]);
            localStorage.setItem("propriedade_selecionada", props[0].id);
          }
        }
      } else {
        // Fallback: direct query
        if (u.email) {
          const props = await agrogestao.entities.Propriedade.filter({ created_by: u.email }).catch(() => []);
          setPropriedades(props);
          if (props.length > 0) {
            const saved = localStorage.getItem("propriedade_selecionada");
            if (saved && props.find(p => p.id === saved)) {
              setPropriedadeSelecionada(props.find(p => p.id === saved));
            } else {
              setPropriedadeSelecionada(props[0]);
              localStorage.setItem("propriedade_selecionada", props[0].id);
            }
          }
        }
      }
    } catch (e) {
      console.warn("Error loading properties:", e.message);
    }
  };

  const handleSelectPropriedade = async (prop) => {
    try {
      // Validar acesso antes de selecionar
      const response = await agrogestao.functions.invoke('validarAcessoPropriedade', {
        propriedade_id: prop.id,
        acao: 'selecao'
      });
      
      if (response.data?.success) {
        setPropriedadeSelecionada(prop);
        localStorage.setItem("propriedade_selecionada", prop.id);
        window.location.reload();
      } else {
        // Fallback: permitir se o usuário for o criador
        const u = await agrogestao.auth.me();
        if (u?.email && prop?.created_by === u.email) {
          setPropriedadeSelecionada(prop);
          localStorage.setItem("propriedade_selecionada", prop.id);
          window.location.reload();
        } else {
          await agrogestao.access.logAudit({ propriedade_id: prop.id, acao: 'selecao', table: 'propriedades', status: 'denied' });
          alert(response.data?.error || 'Acesso negado');
        }
      }
    } catch (e) {
      // Fallback em caso de erro (ex.: CORS na Edge Function)
      try {
        const u = await agrogestao.auth.me();
        if (u?.email && prop?.created_by === u.email) {
          setPropriedadeSelecionada(prop);
          localStorage.setItem("propriedade_selecionada", prop.id);
          window.location.reload();
          return;
        }
        await agrogestao.access.logAudit({ propriedade_id: prop.id, acao: 'selecao', table: 'propriedades', status: 'denied' });
        alert('Acesso negado');
      } catch (e2) {
        console.error("Erro ao selecionar propriedade:", e2);
      }
    }
  };

  const handleLogout = async () => {
    await agrogestao.auth.signOut();
    window.location.href = "/";
  };

  const menuItems = [
    { name: "Dashboard", icon: LayoutDashboard, page: "Dashboard" },
    { name: "Propriedades", icon: Landmark, page: "Propriedades" },
    { name: "Talhões", icon: MapPin, page: "Talhoes" },
    { name: "Rebanho", icon: Tractor, page: "Animais" },
    { name: "Leite", icon: Sparkles, page: "ProducaoLeite" },
    { name: "Parceiros", icon: User, page: "Parceiros" },
    { name: "Financeiro", icon: Wallet, page: "Financeiro" },
    { name: "Contas a Pagar", icon: Receipt, page: "ContasPagar" },
    { name: "Contas a Receber", icon: TrendingUp, page: "ContasReceber" },
    { name: "Financiamentos", icon: Landmark, page: "Financiamentos" },
    { name: "Orçamento", icon: Calculator, page: "Orcamento" },
    { name: "Safras", icon: Wheat, page: "Safras" },
    { name: "Armazéns/Grãos", icon: Package, page: "ArmazensGraos" },
    { name: "Estoque Insumos", icon: Package, page: "Estoque" },
    { name: "Ativos", icon: Truck, page: "Ativos" },
    { name: "Custos", icon: Calculator, page: "Custos" },
    { name: "Relatórios", icon: FileText, page: "Relatorios" },
    { name: "LCDPR", icon: FileCheck, page: "LCDPR" },
    { name: "Simulador IA", icon: Sparkles, page: "Simulador" },
    { name: "Perfil do Produtor", icon: User, page: "PerfilProdutor" },
  ];

  const isLanding = currentPageName === 'Landing';
  const isMarketing = ['Landing','Home','Sobre','Modulos','Contato'].includes(currentPageName);
  const isAdminPage = currentPageName === 'Admin';

  if (isMarketing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
        <style>{`
        :root {
          --verde-escuro: #1B4332;
          --verde-medio: #2D6A4F;
          --verde-claro: #40916C;
          --dourado: #D4A373;
          --dourado-escuro: #BC8A5F;
          --terra: #8B7355;
        }
        `}</style>

        <TopNav />

        <main className="pt-20">
          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>

        <footer className="border-t border-stone-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-1 md:grid-cols-4 gap-6 text-sm">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Tractor className="w-5 h-5 text-[#1B4332]" />
                <span className="font-semibold text-stone-800">AgroGestão</span>
              </div>
              <p className="text-stone-600">Gestão rural inteligente e acessível.</p>
            </div>
            <div>
              <p className="font-semibold text-stone-800 mb-2">Legal</p>
              <div className="space-y-1 text-stone-600">
                <a href="#" className="hover:underline">Termos de Uso</a>
                <a href="#" className="hover:underline">Política de Privacidade</a>
              </div>
            </div>
            <div>
              <p className="font-semibold text-stone-800 mb-2">Suporte</p>
              <div className="space-y-1 text-stone-600">
                <a href="/#contato" className="hover:underline">Contato</a>
                <a href="/?action=signin" className="hover:underline">Entrar</a>
              </div>
            </div>
            <div>
              <p className="font-semibold text-stone-800 mb-2">Redes</p>
              <div className="space-y-1 text-stone-600">
                <a href="#" className="hover:underline">Instagram</a>
                <a href="#" className="hover:underline">LinkedIn</a>
              </div>
            </div>
          </div>
          <div className="text-center text-xs text-stone-500 pb-6">© {new Date().getFullYear()} AgroGestão. Todos os direitos reservados.</div>
        </footer>
      </div>
    );
  }

  if (isAdminPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
        <style>{`
          :root {
            --verde-escuro: #1B4332;
            --verde-medio: #2D6A4F;
            --verde-claro: #40916C;
            --dourado: #D4A373;
            --dourado-escuro: #BC8A5F;
            --terra: #8B7355;
          }
        `}</style>
        <main className="min-h-screen">
          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      <style>{`
        :root {
          --verde-escuro: #1B4332;
          --verde-medio: #2D6A4F;
          --verde-claro: #40916C;
          --dourado: #D4A373;
          --dourado-escuro: #BC8A5F;
          --terra: #8B7355;
        }
      `}</style>

      {/* Busca Global */}
      <BuscaGlobal open={buscaOpen} onOpenChange={setBuscaOpen} />

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-stone-100 rounded-lg">
          <Menu className="w-6 h-6 text-stone-700" />
        </button>
        <div className="flex items-center gap-2">
          <Tractor className="w-7 h-7 text-[#1B4332]" />
          <span className="font-bold text-lg text-[#1B4332]">AgroGestão</span>
        </div>
        <button onClick={() => setBuscaOpen(true)} className="p-2 hover:bg-stone-100 rounded-lg">
          <Search className="w-5 h-5 text-stone-600" />
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 bg-[#1B4332] z-50 transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4A373] rounded-xl flex items-center justify-center">
                  <Tractor className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl text-white">AgroGestão</h1>
                  <p className="text-xs text-white/60">Gestão Rural Simplificada</p>
                </div>
              </div>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1 hover:bg-white/10 rounded"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Property Selector */}
          {propriedades.length > 0 && (
            <div className="px-4 py-4 border-b border-white/10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between px-4 py-3 bg-white/10 hover:bg-white/15 rounded-xl transition-colors">
                    <div className="flex items-center gap-3 text-left">
                      <Landmark className="w-5 h-5 text-[#D4A373]" />
                      <div>
                        <p className="text-sm font-medium text-white truncate max-w-[140px]">
                          {propriedadeSelecionada?.nome || "Selecionar"}
                        </p>
                        <p className="text-xs text-white/50">
                          {propriedadeSelecionada?.area_total ? `${propriedadeSelecionada.area_total} ha` : "Propriedade"}
                        </p>
                      </div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-white/50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60">
                  {propriedades.map((prop) => (
                    <DropdownMenuItem 
                      key={prop.id}
                      onClick={() => handleSelectPropriedade(prop)}
                      className={prop.id === propriedadeSelecionada?.id ? "bg-stone-100" : ""}
                    >
                      <Landmark className="w-4 h-4 mr-2" />
                      <span>{prop.nome}</span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={createPageUrl("Propriedades")} className="flex items-center">
                      <span className="text-[#40916C]">+ Nova propriedade</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
              )}

              {/* Search Button */}
              <div className="px-4 py-2">
              <button
              onClick={() => setBuscaOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-2.5 bg-white/10 hover:bg-white/15 rounded-xl text-white/70 text-sm transition-colors"
              >
              <Search className="w-4 h-4" />
              <span className="flex-1 text-left">Buscar...</span>
              <kbd className="text-xs bg-white/10 px-1.5 py-0.5 rounded">⌘K</kbd>
              </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 px-4 py-4 overflow-y-auto">
            <div className="space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.page}
                  to={createPageUrl(item.page)}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                    ${currentPageName === item.page 
                      ? 'bg-[#D4A373] text-white shadow-lg shadow-[#D4A373]/25' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
            </div>
          </nav>

          {/* User Section */}
          {user && (
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 bg-[#40916C] rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.full_name || "Produtor"}</p>
                  <p className="text-xs text-white/50 truncate">{user.email}</p>
                </div>
                <button 
                  onClick={handleLogout}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Sair"
                >
                  <LogOut className="w-4 h-4 text-white/70" />
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
